/* ifdp-sizeguide.js - Guida alle taglie dinamica (ifiglidipapa / Ifdp)
 *
 * Il popup-size-guide.liquid espone i dati del prodotto come data-attributes
 * sul nodo .ifdp-sg[data-sg-*]. Qui:
 *   1) carichiamo l'asset JSON (una volta, cache),
 *   2) risolviamo brand (vendor->brand), macro (productType->footwear/denim/apparel),
 *      fascia/genere (tag GENERE_/SUBGENERE_),
 *   3) selezioniamo la tabella giusta e la rendiamo nel pannello.
 * Nessuna taglia è hardcoded: tutto viene dall'asset.
 *
 * Si aggancia sia al primo render sia all'apertura del popup nativo Shella
 * (il popup è data-js-popup-name="size-guide"). Idempotente.
 */
(function () {
  'use strict';

  var ASSET_CACHE = null;
  var ASSET_PROMISE = null;

  function norm(s) {
    return (s || '')
      .toString()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function loadAsset(url) {
    if (ASSET_CACHE) return Promise.resolve(ASSET_CACHE);
    if (ASSET_PROMISE) return ASSET_PROMISE;
    ASSET_PROMISE = fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (j) { ASSET_CACHE = j; return j; });
    return ASSET_PROMISE;
  }

  // Quick vendor -> canonical brand key present in asset.brands
  function resolveBrand(asset, vendor) {
    var key = norm(vendor);
    var map = asset.vendor_to_brand || {};
    if (map[key]) return map[key];
    // fallback: try direct match on brand keys by normalized name
    var brands = asset.brands || {};
    for (var b in brands) {
      if (norm(b) === key) return b;
    }
    // partial contains (e.g. "emporioarmanikids" contains "armani")
    for (var k in map) {
      if (key.indexOf(k) !== -1) return map[k];
    }
    return null;
  }

  // productType (italiano) -> macro bucket
  function resolveMacro(asset, type) {
    var t = norm(type);
    var map = asset.type_to_macro || {};
    // explicit map first (scarpe->footwear, jeans->denim)
    for (var k in map) {
      if (t.indexOf(k) !== -1) return map[k];
    }
    // shoe synonyms safety net
    if (/scarp|sneaker|sandal|stival|ballerin|mocassin|calzatur|ciabatt/.test(t)) return 'footwear';
    return 'apparel';
  }

  // GENERE_ tag -> { adult: bool, baby: bool, gender: 'men'|'women'|null }
  function resolveBand(genere, subgenere) {
    var g = norm(genere);
    var sg = norm(subgenere);
    var baby = (g === 'neonati' || g === 'neonato' || g === 'neonata');
    // adult only matters for denim brands (Jeckerson/Roy) - Teen non è adulto qui
    var gender = null;
    if (sg === 'maschio' || sg === 'bambino' || sg === 'ragazzo') gender = 'men';
    else if (sg === 'femmina' || sg === 'bambina' || sg === 'ragazza') gender = 'women';
    return { baby: baby, gender: gender, genere: g, subgenere: sg };
  }

  // pick a section for the brand. For adult denim brands the sections are keyed
  // by gender (men/women). For kids brands the section is usually 'kids' (or
  // women/junior for the off-editoriale luxury ones).
  function pickSection(brandData, band, macro) {
    var secs = brandData.sections || {};
    var keys = Object.keys(secs);
    if (!keys.length) return null;

    // denim adult: prefer matching gender section
    if (macro === 'denim') {
      if (band.gender && secs[band.gender]) return secs[band.gender];
      // some brands only have 'men'
      if (secs.men) return secs.men;
      if (secs.women) return secs.women;
    }
    // kids/apparel/footwear: prefer kids, then junior, then the only one
    if (secs.kids) return secs.kids;
    if (secs.junior) return secs.junior;
    if (secs.women) return secs.women; // e.g. Anonyme (donna)
    return secs[keys[0]];
  }

  // among the tables in a bucket, choose by age band (baby vs kid) using the
  // first column / row contents. Baby tables mention "mth"/"mesi".
  function pickTable(tables, band) {
    if (!tables || !tables.length) return null;
    if (tables.length === 1) return tables[0];
    function isBaby(t) {
      var blob = JSON.stringify(t.columns) + JSON.stringify(t.rows.slice(0, 3));
      return /mth|mesi|month/i.test(blob);
    }
    var babyT = tables.filter(isBaby);
    var kidT = tables.filter(function (t) { return !isBaby(t); });
    if (band.baby && babyT.length) return babyT[0];
    if (!band.baby && kidT.length) return kidT[0];
    return tables[0];
  }

  // Mercato italiano: niente pollici/inches. Scarta le colonne in inch
  // (Height (in)/(Inches), Foot Length (in), Waist (in), Head Circumference (in),
  //  "in" da solo, e "Jeans (Waist)" che sono waist in pollici 24/25/26...).
  function isInchesCol(c) {
    var h = (c || '').split('\n')[0].trim().toLowerCase();
    return /\((in|inch|inches)\)/.test(h)        // "... (in)" / "(inches)"
        || h === 'in'                             // colonna "in" da sola
        || /jeans\s*\(waist\)/.test(h);           // waist jeans in pollici
  }

  // Solo italiano: traduci le INTESTAZIONI (i dati editoriale luxury sono EN). Mappa per header
  // completo (case-insensitive); gli inches sono gia' filtrati a monte.
  var HEADER_IT = {
    'age': 'Età',
    'age (month/years)': 'Età (mesi/anni)',
    'age (months)': 'Età (mesi)',
    'age (years)': 'Età (anni)',
    'baby age (months)': 'Età neonato (mesi)',
    'baby dual age (months)': 'Età neonato (mesi)',
    'dual age (months)': 'Età (mesi)',
    'kids age (years)': 'Età (anni)',
    'brim (cm)': 'Tesa (cm)',
    'circumference (cm)': 'Circonferenza (cm)',
    'china (cm)': 'Cina (cm)',
    'head circumference (cm)': 'Circonferenza testa (cm)',
    'foot length (cm)': 'Lunghezza piede (cm)',
    'height (cm)': 'Altezza (cm)',
    'length (cm)': 'Lunghezza (cm)',
    'waist (cm)': 'Vita (cm)',
    'waist cm': 'Vita (cm)',
    'width (cm)': 'Larghezza (cm)',
    'cm': 'cm',
    'dual uk': 'UK',
    'dual us': 'US',
    'europe': 'Europa',
    'france': 'Francia',
    'italy': 'Italia',
    'japan': 'Giappone',
    'internazionale': 'Internazionale',
    'mid standard': 'Standard',
    'numeric': 'Numerica',
    'roman numeric': 'Numerazione romana',
    'standard': 'Standard',
    'sole height': 'Altezza suola',
    'size 27 eu': 'Taglia EU'
    // EU, EU/FR, UK, US, IT, FR, Taglia, Età, Altezza/Torace/Vita/Fianchi (cm) restano invariati
  };
  function localizeHeader(h) {
    var key = (h || '').trim().toLowerCase();
    return HEADER_IT[key] || h;
  }

  // Solo italiano: traduci i VALORI a regole (unita'/parole), lasciando intatti numeri,
  // taglie lettera (XS/S/M/L/XL), codici US scarpa (5K/2T/Kids/Tod/Inf) e simboli.
  function localizeValue(v) {
    if (v == null) return '';
    var s = String(v).trim();
    if (!/[a-zA-Z]/.test(s)) return s;            // puro numero/codice -> invariato
    s = s.replace(/\bNew\s*born\b/gi, 'Neonato')
         .replace(/\bNewborn\b/gi, 'Neonato')
         .replace(/\bUp to\b/gi, 'Fino a')
         .replace(/\bYears\b/gi, 'anni').replace(/\byrs?\b/gi, 'anni')
         .replace(/\bMonths\b/gi, 'mesi').replace(/\bmths?\b/gi, 'mesi').replace(/\bmth\b/gi, 'mesi')
         .replace(/\bMesi\b/g, 'mesi').replace(/\bAnni\b/g, 'anni');
    return s;
  }

  function buildTableHTML(table) {
    if (!table || !table.columns || !table.rows) return '';
    var cols = table.columns;
    // togli le colonne in pollici, ma NON svuotare la tabella: se il filtro lascia
    // <2 colonne (raro), tieni l'originale.
    var filtered = cols.filter(function (c) { return !isInchesCol(c); });
    if (filtered.length >= 2) cols = filtered;
    var html = '<div class="ifdp-sg__table-wrap" tabindex="0"><table class="ifdp-sg__table"><thead><tr>';
    cols.forEach(function (c) {
      html += '<th scope="col">' + escapeHTML(localizeHeader(cleanCol(c))) + '</th>';
    });
    html += '</tr></thead><tbody>';
    table.rows.forEach(function (row) {
      html += '<tr>';
      cols.forEach(function (c, i) {
        var v = localizeValue(row[c]);
        if (i === 0) html += '<th scope="row">' + escapeHTML(v) + '</th>';
        else html += '<td>' + escapeHTML(v) + '</td>';
      });
      html += '</tr>';
    });
    html += '</tbody></table></div>';
    return html;
  }

  function cleanCol(c) {
    // columns sometimes carry "\n(Brand Kids)" - keep first line only for header
    return c.split('\n')[0].trim();
  }
  function escapeHTML(s) {
    return (s == null ? '' : String(s)).replace(/[&<>"]/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m];
    });
  }

  function render(root) {
    if (!root || root.getAttribute('data-sg-done') === '1') return;
    if (root.querySelector('[data-sg-manual]')) { root.setAttribute('data-sg-done', '1'); return; }
    var panel = root.querySelector('[data-sg-panel]');
    var fallback = root.querySelector('[data-sg-fallback]');
    var assetUrl = root.getAttribute('data-sg-asset');
    if (!panel || !assetUrl) return;
    root.setAttribute('data-sg-done', '1');

    loadAsset(assetUrl).then(function (asset) {
      var vendor = root.getAttribute('data-sg-brand');
      var type = root.getAttribute('data-sg-type');
      var genere = root.getAttribute('data-sg-genere');
      var subgenere = root.getAttribute('data-sg-subgenere');

      var brandKey = resolveBrand(asset, vendor);
      var brandData = brandKey && asset.brands[brandKey];
      if (!brandData) return showFallback();

      var macro = resolveMacro(asset, type);
      var band = resolveBand(genere, subgenere);
      var sec = pickSection(brandData, band, macro);
      if (!sec) return showFallback();

      // choose bucket: footwear/denim if available for that macro, else apparel
      var bucket = sec.tables || {};
      var tables = [];
      if (macro === 'footwear' && bucket.footwear && bucket.footwear.length) tables = bucket.footwear;
      else if (macro === 'denim' && bucket.denim && bucket.denim.length) tables = bucket.denim;
      else tables = bucket.apparel || [];
      // graceful: if a kids "jeans" had no denim bucket, fall back to apparel
      if (!tables.length) tables = bucket.apparel || bucket.footwear || bucket.denim || [];
      if (!tables.length) return showFallback();

      var table = pickTable(tables, band);
      if (!table) return showFallback();

      panel.innerHTML = buildTableHTML(table);
      panel.setAttribute('aria-busy', 'false');

      // brand display label cleanup (use canonical brand key)
      var brandLabel = root.querySelector('[data-sg-brand-label]');
      if (brandLabel) brandLabel.textContent = brandKey;
      var brandMeta = root.querySelector('.ifdp-sg__product-brand');
      if (brandMeta) brandMeta.textContent = brandKey;

      // generic-women note (Anonyme)
      if (sec.is_generic) {
        var note = document.createElement('p');
        note.className = 'ifdp-sg__note';
        note.textContent = '*Tabella indicativa donna (taglie standard italiane).';
        panel.appendChild(note);
      }

      function nothing() {}
      nothing();
    }).catch(showFallback);

    function showFallback() {
      if (panel) { panel.innerHTML = ''; panel.setAttribute('aria-busy', 'false'); }
      if (fallback) fallback.hidden = false;
    }
  }

  function scan() {
    var roots = document.querySelectorAll('.ifdp-sg[data-sg-asset]');
    for (var i = 0; i < roots.length; i++) render(roots[i]);
  }

  // initial + on size-guide popup open (Shella toggles class on the popup body)
  function init() {
    scan();
    // when the user clicks the size-guide trigger, Shella reveals the popup;
    // re-scan shortly after to cover lazy/cloned (footbar) instances.
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-js-popup-button="size-guide"]');
      if (t) setTimeout(scan, 60);
    }, true);
    // MutationObserver: footbar clone / quick-view inject popup later
    if (window.MutationObserver) {
      var mo = new MutationObserver(function () { scan(); });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
